import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usuariosApi, CreateUserPayload, UserSortField } from '../api/usuarios'
import { User } from '../types/auth'
import UsuariosTable from '../components/Usuarios/UsuariosTable'
import UsuarioForm, { UsuarioFormSubmitData } from '../components/Usuarios/UsuarioForm'
import VincularEmpresasDialog from '../components/Usuarios/VincularEmpresasDialog'
import { gruposApi } from '../api/grupos'
import { Grupo } from '../types/api'
import { createLogger } from '../utils/logger'
import ExportButton from '../components/ExportButton'
import AddButton from '../components/AddButton'
import ActionBar from '../components/ActionBar'

const logger = createLogger('UsuariosPage')

const UsuariosPage: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [selectedUsuario, setSelectedUsuario] = useState<User | null>(null)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('edit')
  const [showInactive, setShowInactive] = useState(false)
  const [selectedGrupoId, setSelectedGrupoId] = useState<number | ''>('')
  const [sortField, setSortField] = useState<UserSortField>('id')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Buscar usuários
  const {
    data: usuarios = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['usuarios', { includeInactive: showInactive, grupoId: selectedGrupoId || null, sortField, sortDirection }],
    queryFn: () => usuariosApi.list({
      includeInactive: showInactive,
      grupoId: selectedGrupoId ? Number(selectedGrupoId) : null,
      sort_by: sortField,
      sort_direction: sortDirection,
    }),
    retry: false, // Não tentar novamente em caso de erro
  })

  // Buscar grupos (para filtro)
  const { data: grupos = [], isLoading: gruposLoading } = useQuery({
    queryKey: ['grupos', { status: 'A' }],
    queryFn: () => gruposApi.list({ status: 'A' }),
    staleTime: 60_000,
  })

  const exportColumns = useMemo(
    () => [
      { key: 'id', label: 'ID' },
      { key: 'name', label: 'Nome' },
      { key: 'email', label: 'Email' },
      { key: 'nivel_acesso', label: 'Nível Acesso' },
      { key: 'is_active', label: 'Status', format: (v: boolean) => (v ? 'Ativo' : 'Inativo') },
      { key: 'created_at', label: 'Criado em' },
    ],
    [],
  )

  // Debug: Log dos dados recebidos
  React.useEffect(() => {
    if (usuarios && usuarios.length > 0) {
      logger.info(`Total de usuários carregados: ${usuarios.length}`)
    } else if (!isLoading && usuarios.length === 0) {
      logger.warn('Nenhum usuário carregado')
    }
    if (error) {
      logger.error('Erro ao carregar usuários:', error)
    }
  }, [usuarios, isLoading, error])

  // Mutação para atualizar usuário
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<User> }) =>
      usuariosApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] })
      setSnackbar({
        open: true,
        message: t('users.updateSuccess', 'Usuário atualizado com sucesso'),
        severity: 'success',
      })
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || t('users.title'),
        severity: 'error',
      })
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateUserPayload) => usuariosApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] })
      setSnackbar({
        open: true,
        message: t('users.createSuccess', 'Usuário criado com sucesso'),
        severity: 'success',
      })
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || t('users.title'),
        severity: 'error',
      })
    },
  })

  const handleEdit = (usuario: User) => {
    setSelectedUsuario(usuario)
    setFormMode('edit')
    setFormOpen(true)
  }

  const handleCreate = () => {
    setSelectedUsuario(null)
    setFormMode('create')
    setFormOpen(true)
  }

  const handleLinkEmpresas = (usuario: User) => {
    setSelectedUsuario(usuario)
    setLinkDialogOpen(true)
  }

  const handleFormSubmit = async (data: UsuarioFormSubmitData) => {
    if (formMode === 'edit' && selectedUsuario) {
      const payload: Partial<User> = {
        name: data.name,
        is_active: data.is_active,
      }
      if (data.nivel_acesso !== undefined && data.nivel_acesso !== null) {
        payload.nivel_acesso = data.nivel_acesso
      }
      await updateMutation.mutateAsync({ id: selectedUsuario.id, data: payload })
    } else if (formMode === 'create' && data.email && data.password) {
      const email = data.email.trim().toLowerCase()
      const payload: CreateUserPayload = {
        name: data.name,
        email,
        password: data.password,
        is_active: data.is_active,
        nivel_acesso: data.nivel_acesso ?? undefined,
      }
      await createMutation.mutateAsync(payload)
    }
  }

  const handleSort = (field: UserSortField) => {
    if (sortField !== field) {
      setSortField(field)
      setSortDirection('asc')
      return
    }
    setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  if (error) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Alert severity="error">{t('common.loading')}: {(error as Error).message}</Alert>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        py: { xs: 1.5, md: 2 },
        px: { xs: 1.5, sm: 2, md: 3 },
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        boxSizing: 'border-box',
      }}
    >
        {/* Barra de botões (padrão grid 4.6; expansão 4.8) */}
        <ActionBar>
          <ExportButton
            data={Array.isArray(usuarios) ? usuarios : []}
            columns={exportColumns}
            filename="usuarios.xlsx"
            sheetName="Usuários"
          />
          <AddButton
            onClick={handleCreate}
            title={t('users.createButton', 'Adicionar Usuário')}
          />
        </ActionBar>

        {/* Barra de filtros */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap" sx={{ flexShrink: 0 }}>
          <FormControl sx={{ minWidth: 260 }} size="small">
            <InputLabel id="grupo-select-label">{t('users.groupFilter', 'Grupo de empresas')}</InputLabel>
            <Select
              labelId="grupo-select-label"
              value={selectedGrupoId}
              label={t('users.groupFilter', 'Grupo de empresas')}
              onChange={(e) => setSelectedGrupoId(e.target.value as number | '')}
              disabled={gruposLoading}
            >
              <MenuItem value="">{t('common.all', 'Todos')}</MenuItem>
              {(grupos as Grupo[]).map((g) => (
                <MenuItem key={g.id} value={g.id}>{g.nome}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={showInactive}
                onChange={(event) => {
                  setShowInactive(event.target.checked)
                }}
              />
            }
            label={t('users.showInactive', 'Mostrar usuários inativos')}
          />
        </Box>

        {/* Corpo: tabela + paginação (expande na altura) */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <UsuariosTable
            usuarios={Array.isArray(usuarios) ? usuarios : []}
            onEdit={handleEdit}
            onLinkEmpresas={handleLinkEmpresas}
            loading={isLoading}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
        </Box>

      <UsuarioForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        usuario={selectedUsuario}
        loading={updateMutation.isPending || createMutation.isPending}
        mode={formMode}
      />

      <VincularEmpresasDialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        usuario={selectedUsuario}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default UsuariosPage
